// API 服務模組
import logger from '../utils/logger';
import * as appStorage from '../utils/appIndexedDB';
import { getDeviceId } from '../utils/deviceId';
import { isMaintenanceResponse, notifyMaintenance } from '../utils/maintenance';
import { MCP_APP_MIME_TYPE } from '../utils/mcpApps';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// 模擬模式：當 REACT_APP_USE_MOCK_AUTH=true 或未設定 API_BASE_URL 時啟用
const USE_MOCK_AUTH = (process.env.REACT_APP_USE_MOCK_AUTH === 'true') || !API_BASE_URL;

// 請求逾時時間（毫秒），可透過環境變數 REACT_APP_REQUEST_TIMEOUT_MS 覆寫，預設 60 秒
const REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_REQUEST_TIMEOUT_MS) || 60000;

const TOKEN_STORAGE_KEY = 'mygenie_token';
const REFRESH_TOKEN_STORAGE_KEY = 'mygenie_refresh_token';
const AUTH_STORAGE_KEY = 'mygenie_auth';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    // MCP Apps 互動畫面 HTML 的記憶體快取（AGUI-1274）：key 為 `${server}|${uri}`、value 為讀取中的 Promise，
    // 同一頁面內同一畫面只讀一次；失敗即移除（不快取失敗）。HTML 約 2MB 且後端標 no-store，
    // 刻意只放記憶體，絕不寫入 IndexedDB／localStorage。
    this.mcpAppResourceCache = new Map();
  }

  // 清除認證狀態並跳轉到登入頁
  clearAuthAndRedirectToLogin() {
    logger.auth('清除認證狀態並跳轉到登入頁');
    Promise.all([
      appStorage.removeItem(TOKEN_STORAGE_KEY),
      appStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY),
      appStorage.removeItem(AUTH_STORAGE_KEY),
    ])
      .catch(() => {})
      .finally(() => {
        window.location.reload();
      });
  }

  /**
   * 顯示與 Sidebar 登出確認相同樣式的重新登入提示，僅在按下「確定」後執行 onConfirm。
   */
  showReauthRequiredDialog(onConfirm) {
    if (typeof document === 'undefined') {
      onConfirm();
      return;
    }
    const overlayId = 'reauth-required-overlay';
    if (document.getElementById(overlayId)) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.id = overlayId;
    overlay.className = 'delete-confirm-overlay';

    const dialog = document.createElement('div');
    dialog.className = 'delete-confirm-dialog';
    dialog.addEventListener('click', (e) => e.stopPropagation());

    const messageEl = document.createElement('div');
    messageEl.className = 'delete-confirm-title';
    messageEl.appendChild(
      document.createTextNode('為了保護您的帳戶安全與資料隱私，需要定期更新連線驗證。')
    );
    messageEl.appendChild(document.createElement('br'));
    messageEl.appendChild(
      document.createTextNode('請重新登入以繼續使用各項 AI 服務。')
    );

    const buttons = document.createElement('div');
    buttons.className = 'delete-confirm-buttons';

    const okBtn = document.createElement('button');
    okBtn.type = 'button';
    okBtn.className = 'delete-confirm-btn confirm-btn';
    okBtn.textContent = '確定';
    okBtn.addEventListener('click', () => {
      overlay.remove();
      onConfirm();
    });

    buttons.appendChild(okBtn);
    dialog.appendChild(messageEl);
    dialog.appendChild(buttons);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }

  // 通用請求方法
  async request(endpoint, options = {}) {
    const { includeHttpMeta = false, ...restOptions } = options;
    const url = `${this.baseURL}${endpoint}`;
    const method = restOptions.method || 'GET';
    
    const deviceId = getDeviceId();
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...(deviceId ? { 'X-Device-Id': deviceId } : {}),
      },
    };

    // 逾時控制：即使呼叫端傳入自己的 signal（例如「停止生成」用的），逾時仍必須生效，
    // 故一律以內部 timeoutController 的 signal 作為 fetch 實際監聽的對象，
    // 呼叫端的 signal 一旦 abort 就連動觸發 timeoutController.abort()（合併兩個 signal）。
    // 不使用 AbortSignal.any()，iOS Safari 17.4 才支援（同 uploadFiles() 的作法）。
    const timeoutMs = restOptions.timeout ?? REQUEST_TIMEOUT_MS;
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

    const externalSignal = restOptions.signal;
    const signal = timeoutController.signal;
    if (externalSignal) {
      if (externalSignal.aborted) {
        clearTimeout(timeoutId);
        throw new DOMException('Request cancelled', 'AbortError');
      }
      const outerAbort = () => timeoutController.abort();
      externalSignal.addEventListener('abort', outerAbort, { once: true });
      timeoutController.signal.addEventListener(
        'abort',
        () => externalSignal.removeEventListener('abort', outerAbort),
        { once: true }
      );
    }

    const config = {
      ...defaultOptions,
      ...restOptions,
      signal,
      headers: {
        ...defaultOptions.headers,
        ...restOptions.headers,
      },
    };

    // 記錄 API 請求：不記 headers 與 body（含 Bearer token、登入密碼、refresh token），
    // console 會被 Sentry 收成 breadcrumb，之後任何一筆錯誤上報都會一起帶上去（AGUI-1276）
    logger.apiRequest(method, url, {
      timeoutMs,
      hasExternalSignal: !!externalSignal,
    });

    try {
      const startTime = Date.now();
      const fetchResponse = await fetch(url, config);
      const endTime = Date.now();
      clearTimeout(timeoutId);
      const duration = endTime - startTime;
      
      // 記錄 API 回應
      logger.apiResponse(method, url, fetchResponse.status, {
        duration: `${duration}ms`,
        ok: fetchResponse.ok,
      });
      
      // 先用.text()查看response body，確認是否為JSON格式
      // 不記 response body：登入、換 token 的回應含 token（AGUI-1276）
      const text = await fetchResponse.text();
      const contentType = fetchResponse.headers.get("content-type");
      logger.info('contentType', `response headerconente: ${contentType}`)

      const httpMeta = {
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        ok: fetchResponse.ok,
        headers: Object.fromEntries(fetchResponse.headers.entries()),
      };

      // 如果是JSON格式，則用JSON.parse()解析
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        const err = new Error(`Invalid JSON response: ${parseErr.message}`);
        err.http = httpMeta;
        err.rawBody = text;
        throw err;
      }
      
      if (!fetchResponse.ok) {
        logger.error('API', `請求失敗: ${method} ${url}`, {
          status: fetchResponse.status,
          statusText: fetchResponse.statusText,
          error: data
        });
        // 後端業務 API 多為 HTTP 200 + ApiResponse.code；此分支多為閘道／代理非 2xx
        const err = new Error(data.message || `HTTP error! status: ${fetchResponse.status}`);
        err.http = httpMeta;
        err.body = data;
        if (typeof data?.code === 'number') {
          err.businessCode = data.code;
        }
        throw err;
      }

      // 後端維護中（BFF MaintenanceFilter 回 HTTP 200 + maintenance:true）：通知 App 顯示維護畫面
      if (isMaintenanceResponse(data)) {
        notifyMaintenance(data.message);
      }

      if (typeof data?.code === 'number' && data.code === 401) {
        // 業務碼 401 = token 過期，是設計上會發生且由呼叫端自動 refresh 復原的正常流程，
        // 不算錯誤；真正 refresh／重送失敗時，呼叫端會自行呼叫 logger.error 上報。
        logger.warn('API', `業務碼 401（token 過期，等待呼叫端 refresh）: ${method} ${url}`, {
          code: data.code,
          message: data.message,
        });
      } else if (typeof data?.code === 'number' && data.code !== 200) {
        logger.error('API', `業務邏輯失敗: ${method} ${url}`, {
          code: data.code,
          message: data.message,
        });
      } else {
        logger.success('API', `請求成功: ${method} ${url}`, {
          duration: `${duration}ms`,
          dataSize: JSON.stringify(data).length
        });
      }

      if (includeHttpMeta) {
        return { body: data, http: httpMeta };
      }
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      const message = signal?.aborted ? '系統無回應或忙碌中，請稍候再試' : error.message;
      logger.error('API', `請求異常: ${method} ${url}`, {
        error: message,
        stack: error.stack
      });
      if (signal?.aborted) throw new Error(message);
      throw error;
    }
  }

  // AD 登入  
  async login(credentials) {
    logger.auth('開始登入 API 呼叫', { 
      username: credentials.username, 
      authMode: credentials.authMode || 'ldap',
      domain: credentials.domain || '未選擇',
      mock: USE_MOCK_AUTH 
    });

    // 根據API規格，路徑為 /web/auth/login
    // 構建請求體，包含 authMode 和 domain（如果提供）
    const requestBody = {
      username: credentials.username,
      password: credentials.password,
    };
    if (credentials.authMode) {
      requestBody.authMode = credentials.authMode;
    }
    if (credentials.domain) {
      requestBody.domain = credentials.domain;
    }
    const response = await this.request('/web/auth/login', {
      method: 'POST', 
      body: JSON.stringify(requestBody),
    });
    // 根據API規格，後端返回 ApiResponse 格式：{ code, message, data, timestamp }
    // 需要轉換為前端期望的格式：{ success, message, data: { user, token } }
    if (response.code === 200 && response.data) {
      return {
        success: true,
        message: response.message || '登入成功',
        data: {
          user: response.data.user,
          token: response.data.token,
          refreshToken: response.data.refreshToken, // 保存 refreshToken
        },
      };
    } else {
      return {
        success: false,
        message: response.message || '登入失敗',
      };
    }
  }

  // SSO 登入 API - 使用 SSO token 取得使用者資訊
  async ssoLogin(ssoToken) {
    logger.auth('開始 SSO 登入 API 呼叫', { hasToken: !!ssoToken, mock: USE_MOCK_AUTH });
    
    try {
      // tokenId 需放在 query string 中
      const endpoint = `/web/getUserInfo?tokenId=${encodeURIComponent(ssoToken)}`;
      const response = await this.request(endpoint, {
        method: 'POST',
      });

      // 根據 API 規格判斷是否成功
      if (response.code === 200 && response.data) {
        return {
          success: true,
          message: response.message || 'SSO 登入成功',
          data: {
            user: response.data.user,
            token: response.data.token,
            refreshToken: response.data.refreshToken,
          },
        };
      }

      return {
        success: false,
        message: response.message || 'SSO 登入失敗',
      };
    } catch (error) {
      logger.error('API', 'SSO 登入請求異常', {
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // M+ 登入 API - 使用 auserid 和 token 進行認證
  async mplusLogin(auserid, token, apiEndpoint = '/mplus/login') {
    logger.auth('開始 M+ 登入 API 呼叫', { 
      auserid, 
      hasToken: !!token,
      apiEndpoint,
      mock: USE_MOCK_AUTH 
    });

    if (USE_MOCK_AUTH) {
      // 模擬 API 延遲
      await new Promise(resolve => setTimeout(resolve, 300));

      // 模擬參數驗證失敗
      if (!auserid || auserid.trim() === '') {
        logger.warn('API', '模擬 M+ 登入失敗：缺少 auserid 參數');
        return {
          success: false,
          message: 'auserid 參數為必填',
        };
      }

      if (!token || token.trim() === '') {
        logger.warn('API', '模擬 M+ 登入失敗：缺少 token 參數');
        return {
          success: false,
          message: 'token 參數為必填',
        };
      }

      // 模擬認證失敗的情況（當 auserid 為特定值時）
      if (auserid === 'invalid_user' || auserid === 'test_fail') {
        logger.warn('API', '模擬 M+ 登入失敗：認證失敗');
        return {
          success: false,
          message: '認證失敗: 查無該員工',
        };
      }

      // 模擬成功的情況，返回格式與 AD 登入一致
      logger.success('API', '模擬 M+ 登入成功', { auserid, apiEndpoint });
      return {
        success: true,
        message: '登入成功',
        data: {
          user: {
            userId: `mock_user_${auserid}`,
            username: `mock_user_${auserid}`,
            displayName: `模擬使用者_${auserid}`,
            email: `mock_${auserid}@example.com`,
            empno: `EMP${auserid.slice(-4)}`,
            authSource: 'mplus',
          },
          token: `mock_token_${auserid}_${Date.now()}`,
          refreshToken: `mock_refresh_token_${auserid}_${Date.now()}`,
        },
      };
    }

    try {
      // 構建查詢參數，必須包含 auserid 和 token
      // 使用傳入的 apiEndpoint 參數
      const endpoint = `${apiEndpoint}?auserid=${encodeURIComponent(auserid)}&token=${encodeURIComponent(token)}`;

      const response = await this.request(endpoint, {
        method: 'POST',
      });

      // 根據API規格，後端返回 ApiResponse 格式：{ code, message, data, timestamp }
      // 需要轉換為前端期望的格式：{ success, message, data: { user, token, refreshToken } }
      if (response.code === 200 && response.data) {
        return {
          success: true,
          message: response.message || 'M+ 登入成功',
          data: {
            user: response.data.user,
            token: response.data.token,
            refreshToken: response.data.refreshToken,
          },
        };
      } else {
        return {
          success: false,
          message: response.message || 'M+ 登入失敗',
        };
      }
    } catch (error) {
      logger.error('API', 'M+ 登入請求異常', {
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // 登出 API
  async logout(token, refreshToken) {
    logger.auth('開始登出 API 呼叫', { 
      mock: USE_MOCK_AUTH,
      hasToken: !!token,
      hasRefreshToken: !!refreshToken
    });
    if (USE_MOCK_AUTH) {
      await new Promise(resolve => setTimeout(resolve, 200));
      logger.success('API', '模擬登出成功');
      return { success: true, message: '模擬登出成功' };
    }
    
    // 根據API規格，路徑為 /web/auth/logout
    // Header 要帶 access token，Body 要帶 refreshToken
    try {
      const response = await this.request('/web/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.code === 200) {
        return {
          success: true,
          message: response.message || '登出成功',
        };
      } else {
        return {
          success: false,
          message: response.message || '登出失敗',
        };
      }
    } catch (error) {
      logger.error('API', '登出請求異常', {
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // 驗證 token API（僅回報目前這個 token 是否有效，不在內部做 refresh；
  // refresh 的職責統一交給呼叫端處理，避免呼叫端無法分辨「本來就有效」跟
  // 「過期但內部已偷偷刷新成功」這兩種情況，導致誤用舊 token）
  async verifyToken(token) {
    logger.auth('開始驗證 Token API 呼叫', { mock: USE_MOCK_AUTH });

    try {
      // 根據API規格，路徑為 /web/auth/verify
      const response = await this.request('/web/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // 轉換 ApiResponse 格式
      if (response.code === 200 && response.data === true) {
        return { success: true };
      }
      // 有收到伺服器回應，且明確判定 token 無效
      return { success: false, networkError: false };
    } catch (error) {
      // request() 只有在真的拿到 HTTP 回應時才會附上 error.http（例如非 2xx、業務碼失敗）；
      // 沒有 error.http 代表根本沒收到伺服器回應（離線、DNS 失敗、逾時/abort），
      // 這種情況不能當作「token 無效」，否則手機網路瞬斷會被誤判成憑證過期。
      const networkError = !error.http;
      logger.warn('API', '驗證 Token API 請求異常', {
        error: error.message,
        networkError,
      });
      return { success: false, networkError };
    }
  }

  
  // 刷新 token API
  async refreshToken(refreshToken) {
    logger.auth('開始刷新 Token API 呼叫', { mock: USE_MOCK_AUTH });
    if (!refreshToken) {
      logger.warn('API', '刷新 Token 失敗：缺少 refreshToken');
      return { success: false, networkError: false, message: '缺少 refresh token' };
    }


    try {
      const response = await this.request('/web/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });

      if (response.code === 200 && response.data?.token) {
        await appStorage.setItem(TOKEN_STORAGE_KEY, response.data.token);
        await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.data.refreshToken || refreshToken);
        
        return {
          success: true,
          token: response.data.token,
          refreshToken: response.data.refreshToken || refreshToken,
        };
      }

      return {
        success: false,
        networkError: false,
        message: response.message || '刷新 Token 失敗',
      };
    } catch (error) {
      // 同 verifyToken()：error.http 存在才代表伺服器真的回覆了拒絕，
      // 沒有 error.http 代表根本沒連上（網路瞬斷/逾時），不可視為 refresh token 真的失效。
      const networkError = !error.http;
      logger.error('API', '刷新 Token API 請求異常', {
        error: error.message,
        stack: error.stack,
        networkError,
      });
      return {
        success: false,
        networkError,
        message: error.message || '刷新 Token 失敗',
      };
    }
  }

  // 取得使用者資訊 API
  async getUserInfo(token) {
    logger.auth('開始取得使用者資訊 API 呼叫', { mock: USE_MOCK_AUTH });
    if (USE_MOCK_AUTH) {
      await new Promise(resolve => setTimeout(resolve, 250));
      // 在模擬模式下回傳基本使用者資訊（從 token 無法還原使用者，只提供範例）
      return {
        success: true,
        data: { role: 'user' },
      };
    }
    // 根據API規格，路徑為 /web/auth/user
    const response = await this.request('/web/auth/user', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    // 轉換 ApiResponse 格式
    if (response.code === 200 && response.data) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      return {
        success: false,
        message: response.message || '取得使用者資訊失敗',
      };
    }
  }

  // 註冊 API（可選）
  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // 重設密碼 API（可選）
  async resetPassword(email) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // === 聊天紀錄相關 API ===

  // 取得所有聊天紀錄
  // TODO: 恢復 GET /chats 後實作
  async getChatHistory(_token) {
    throw new Error('getChatHistory not implemented');
  }

  // 創建新對話
  async createChat(token, chatData) {
    logger.info('聊天', '開始創建新對話 API 呼叫', { title: chatData.title });
    return this.request('/mgr/chats', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(chatData),
    });
  }

  // 更新對話（標題或訊息）
  async updateChat(token, chatId, updateData) {
    logger.info('聊天', '開始更新對話 API 呼叫', { chatId, updateType: Object.keys(updateData) });
    return this.request(`/chats/${chatId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updateData),
    });
  }

  /**
   * 建立或更新訊息回饋（ag_tb_message_feedback）
   * 與 sendMessageExternal 相同：從 storage 讀取 token，僅在 HTTP 200 且業務碼 401 時 refresh 並重送一次。
   * @param {boolean|null|undefined} payload.userFeedback — true 按讚、false 倒讚、null 清除
   * @param {string|null|undefined} payload.userComment — 使用者補充說明
   * @returns {Promise<{code:number,message?:string,data?:*}|undefined>} 需重新登入時可能為 undefined（將觸發導向登入）
   */
  async submitMessageFeedback({ chatId, messageId, userFeedback, userComment, commentOnly }) {
    logger.info('聊天', '提交訊息回饋', { chatId, messageId, userFeedback, userComment, commentOnly });

    const canAccessStorage = typeof window !== 'undefined';
    if (!canAccessStorage) {
      logger.error('聊天', '執行環境無法存取 storage，終止提交訊息回饋');
      throw new Error('瀏覽器環境異常，無法存取憑證');
    }

    let accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    let refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!accessToken) {
      logger.error('聊天', '缺少 access token，無法提交訊息回饋');
      throw new Error('尚未登入或 token 遺失，請重新登入');
    }

    const body = {
      chatId,
      messageId,
      userFeedback: userFeedback === undefined ? null : userFeedback,
      ...(commentOnly ? { commentOnly: true } : {}),
      // 包含空字串：與前端「未填留言仍送出」一致
      ...(commentOnly && userComment !== undefined && userComment !== null
        ? { userComment: String(userComment) }
        : {}),
    };

    const buildFeedbackRequest = (token) => ({
      url: `${this.baseURL}/web/messages/feedback`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body,
    });

    const submitFeedbackOnce = async (token) => {
      const requestResult = await this.request('/web/messages/feedback', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        includeHttpMeta: true,
      });
      return {
        response: requestResult.body,
        apiResponse: {
          body: requestResult.body,
          http: requestResult.http,
        },
        requestSnapshot: buildFeedbackRequest(token),
      };
    };

    const buildBusinessError = (response, apiResponse, requestSnapshot) => {
      const err = new Error(response.message || '提交訊息回饋失敗');
      err.requestSnapshot = requestSnapshot;
      err.apiResponse = apiResponse;
      err.businessCode = response.code;
      return err;
    };

    let lastRequestSnapshot = buildFeedbackRequest(accessToken);

    try {
      logger.info('聊天', '開始第一次提交訊息回饋');
      const firstSend = await submitFeedbackOnce(accessToken);
      lastRequestSnapshot = firstSend.requestSnapshot;

      if (firstSend.response.code === 200) {
        logger.info('聊天', '第一次提交訊息回饋成功');
        return firstSend.response;
      }

      if (firstSend.apiResponse.http?.status === 200 && firstSend.response.code === 401) {
        logger.warn('聊天', '第一次提交訊息回饋命中 401，進入 refresh token 流程');
        if (!refreshToken) {
          logger.error('聊天', '訊息回饋回傳 401 且缺少 refresh token，跳轉到登入頁');
          this.clearAuthAndRedirectToLogin();
          return;
        }

        logger.info('聊天', '開始呼叫 refresh token API');
        const refreshResult = await this.request('/web/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
          includeHttpMeta: true,
        });
        const refreshResponse = refreshResult.body;
        const refreshHttpStatus = refreshResult.http?.status;
        logger.info('聊天', 'refresh token API 已回應', {
          httpStatus: refreshHttpStatus,
          responseCode: refreshResponse?.code,
        });

        if (refreshHttpStatus === 200 && refreshResponse.code === 200 && refreshResponse.data?.token) {
          logger.info('聊天', 'refresh token 成功，準備更新本地 token');
          accessToken = refreshResponse.data.token;
          refreshToken = refreshResponse.data.refreshToken || refreshToken;
          await appStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
          await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
          logger.info('聊天', '本地 token 更新完成，準備重送訊息回饋');

          logger.info('聊天', '開始第二次提交訊息回饋（重送）');
          const secondSend = await submitFeedbackOnce(accessToken);
          lastRequestSnapshot = secondSend.requestSnapshot;
          if (secondSend.response.code === 200) {
            logger.info('聊天', '第二次提交訊息回饋成功');
            return secondSend.response;
          }

          logger.error('聊天', '刷新 token 後重送訊息回饋失敗', {
            status: secondSend.response.code,
            message: secondSend.response.message,
          });
          throw buildBusinessError(secondSend.response, secondSend.apiResponse, secondSend.requestSnapshot);
        }

        logger.error('聊天', '刷新 token 失敗，跳轉到登入頁', {
          httpStatus: refreshHttpStatus,
          status: refreshResponse.code,
          message: refreshResponse.message,
        });
        this.showReauthRequiredDialog(() => this.clearAuthAndRedirectToLogin());
        return;
      }

      logger.warn('聊天', '第一次提交訊息回饋失敗且不符合 refresh 條件', {
        httpStatus: firstSend.apiResponse.http?.status,
        status: firstSend.response.code,
      });
      throw buildBusinessError(firstSend.response, firstSend.apiResponse, firstSend.requestSnapshot);
    } catch (error) {
      if (!error.requestSnapshot) {
        error.requestSnapshot = lastRequestSnapshot;
      }
      if (!error.apiResponse && (error.http || Object.prototype.hasOwnProperty.call(error, 'body') || Object.prototype.hasOwnProperty.call(error, 'rawBody'))) {
        error.apiResponse = {
          body: error.body ?? null,
          http: error.http ?? null,
          ...(error.rawBody !== undefined ? { rawBody: error.rawBody } : {}),
        };
      }
      logger.error('聊天', '提交訊息回饋異常', { error: error.message, chatId, messageId });
      throw error;
    }
  }

  async getSysParams(paramType, paramSubType) {
    logger.info('系統參數', '取得系統參數列表', { paramType, paramSubType });
    const token = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) return [];
    const query = paramSubType
      ? `paramType=${encodeURIComponent(paramType)}&paramSubType=${encodeURIComponent(paramSubType)}`
      : `paramType=${encodeURIComponent(paramType)}`;
    try {
      const result = await this.request(`/web/sys-params?${query}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (result?.code === 200 && Array.isArray(result?.data)) {
        return result.data;
      }
      return [];
    } catch {
      return [];
    }
  }

  // 刪除聊天
  async deleteChat(token, chatId) {
    logger.info('聊天', '開始刪除聊天 API 呼叫', { chatId });
    return this.request(`/mgr/chats/${chatId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  // 取得特定聊天的詳細資訊
  async getChat(token, chatId) {
    logger.info('聊天', '開始取得聊天詳細資訊 API 呼叫', { chatId });
    return this.request(`/chats/${chatId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  // 同步所有聊天紀錄到後端
  async syncAllChats(token, chatHistory) {
    logger.info('聊天', '開始同步所有聊天紀錄到後端 API 呼叫', { chatCount: chatHistory.length });
    return this.request('/chats/sync', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ chatHistory }),
    });
  }

  // 創建新對話 - 使用後端 API
  async createNewChatExternal() {
    logger.info('聊天', '開始創建新對話 API 呼叫');

    const canAccessStorage = typeof window !== 'undefined';
    if (!canAccessStorage) {
      throw new Error('瀏覽器環境異常，無法存取憑證');
    }

    let accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    let refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!accessToken) {
      logger.error('聊天', '缺少 access token，無法建立聊天');
      throw new Error('尚未登入或 token 遺失，請重新登入');
    }

    try {
      const firstCreateResult = await this.request('/web/chat/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        includeHttpMeta: true,
      });
      const firstResponse = firstCreateResult.body;
      const firstHttpStatus = firstCreateResult.http?.status;

      if (firstResponse.code === 200 && firstResponse.data) {
        logger.success('聊天', '創建新對話成功', {
          chatId: firstResponse.data.data?.chat_id,
          assistantId: firstResponse.data.data?.assistant_id
        });
        return firstResponse;
      }

      // 先送創建請求，僅在 HTTP 200 且業務碼 401 時才刷新 token 並重送一次
      if (firstHttpStatus === 200 && firstResponse.code === 401) {
        if (!refreshToken) {
          logger.error('聊天', '創建對話回傳 401 且缺少 refresh token，跳轉到登入頁');
          this.showReauthRequiredDialog(() => this.clearAuthAndRedirectToLogin());
          return;
        }

        const refreshResult = await this.request('/web/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
          includeHttpMeta: true,
        });
        const refreshResponse = refreshResult.body;
        const refreshHttpStatus = refreshResult.http?.status;

        if (refreshHttpStatus === 200 && refreshResponse.code === 200 && refreshResponse.data?.token) {
          accessToken = refreshResponse.data.token;
          refreshToken = refreshResponse.data.refreshToken || refreshToken;
          await appStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
          await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);

          const secondCreateResult = await this.request('/web/chat/create', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
            includeHttpMeta: true,
          });
          const secondResponse = secondCreateResult.body;

          if (secondResponse.code === 200 && secondResponse.data) {
            logger.success('聊天', '刷新 token 後創建新對話成功', {
              chatId: secondResponse.data.data?.chat_id,
              assistantId: secondResponse.data.data?.assistant_id
            });
            return secondResponse;
          }

          logger.error('聊天', '刷新 token 後創建新對話失敗', {
            status: secondResponse.code,
            message: secondResponse.message
          });
          const secondCreateErr = new Error(secondResponse.message || '創建新對話失敗');
          secondCreateErr.businessCode = secondResponse.code;
          throw secondCreateErr;
        }

        logger.error('聊天', '刷新 token 失敗，跳轉到登入頁', {
          httpStatus: refreshHttpStatus,
          status: refreshResponse.code,
          message: refreshResponse.message
        });
        this.showReauthRequiredDialog(() => this.clearAuthAndRedirectToLogin());
        return;
      }

      logger.error('聊天', '創建新對話失敗', {
        status: firstResponse.code,
        message: firstResponse.message
      });
      const createErr = new Error(firstResponse.message || '創建新對話失敗');
      createErr.businessCode = firstResponse.code;
      throw createErr;
    } catch (error) {
    
      logger.error('聊天', '創建新對話異常', { error: error.message });
      throw error;
    }
  }

  // 消費推播深連結（change: notification-deeplink-chat-trigger）
  // 後端僅解密驗證 payload，回傳 { prompt, channel }；不建立對話、不呼叫 GenAIus——
  // 呼叫端（App.js）拿到後要自行呼叫 useChat 的 sendMessage 重用一般送訊息流程。
  // 純解密動作很快，沿用預設的 REQUEST_TIMEOUT_MS 即可，不需要拉長 timeout。
  async consumeChatLink(payload) {
    logger.info('聊天', '開始消費推播深連結');

    let accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    let refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!accessToken) {
      logger.error('聊天', '缺少 access token，無法消費深連結');
      throw new Error('尚未登入或 token 遺失，請重新登入');
    }

    try {
      const firstResult = await this.request('/web/chat/link/consume', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ payload }),
        includeHttpMeta: true,
      });
      const firstResponse = firstResult.body;

      if (firstResponse.code === 200 && firstResponse.data) {
        logger.success('聊天', '消費深連結成功', { chatId: firstResponse.data.chatId });
        return firstResponse.data;
      }

      // 僅在 HTTP 200 且業務碼 401 時才刷新 token 並重送一次，與 createNewChatExternal 一致
      if (firstResult.http?.status === 200 && firstResponse.code === 401) {
        if (!refreshToken) {
          this.showReauthRequiredDialog(() => this.clearAuthAndRedirectToLogin());
          throw new Error('尚未登入或 token 遺失，請重新登入');
        }

        const refreshResult = await this.request('/web/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
          includeHttpMeta: true,
        });
        const refreshResponse = refreshResult.body;

        if (refreshResult.http?.status === 200 && refreshResponse.code === 200 && refreshResponse.data?.token) {
          accessToken = refreshResponse.data.token;
          refreshToken = refreshResponse.data.refreshToken || refreshToken;
          await appStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
          await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);

          const secondResult = await this.request('/web/chat/link/consume', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ payload }),
            includeHttpMeta: true,
          });
          const secondResponse = secondResult.body;

          if (secondResponse.code === 200 && secondResponse.data) {
            logger.success('聊天', '刷新 token 後消費深連結成功', { chatId: secondResponse.data.chatId });
            return secondResponse.data;
          }

          const secondErr = new Error(secondResponse.message || '消費深連結失敗');
          secondErr.businessCode = secondResponse.code;
          throw secondErr;
        }

        this.showReauthRequiredDialog(() => this.clearAuthAndRedirectToLogin());
        throw new Error('重新整理登入狀態失敗');
      }

      logger.error('聊天', '消費深連結失敗', { status: firstResponse.code, message: firstResponse.message });
      const err = new Error(firstResponse.message || '連結無效或已失效');
      err.businessCode = firstResponse.code;
      throw err;
    } catch (error) {
      logger.error('聊天', '消費深連結異常', { error: error.message });
      throw error;
    }
  }

  /**
   * 上傳檔案至 BFF，取得 AI Agent attachment URL 陣列。
   * 首次遇到 401（token 過期）時自動 refresh 並重試一次；refresh 失敗則顯示重新登入提示。
   *
   * @param {File[]} files              要上傳的檔案陣列
   * @param {string} sessionId          GenAIus session ID（即 chatId）
   * @param {AbortSignal} [externalSignal] 外部取消信號（由 useFileUpload 傳入）
   * @returns {Promise<{attachments: string[], sessionId: string}>} AI Agent 回傳的 attachment URLs
   */
  async uploadFiles(files, sessionId, externalSignal) {
    logger.info('聊天', '開始上傳檔案', { fileCount: files.length, sessionId });

    let accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!accessToken) {
      throw new Error('尚未登入或 token 遺失，請重新登入');
    }

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    // sessionId 以 query param 方式傳遞（對應後端 @RequestParam）
    const url = `${this.baseURL}/web/file/upload?sessionId=${encodeURIComponent(sessionId)}`;

    // 執行一次 multipart 上傳，回傳 { httpStatus, data }
    const doFetch = async (token) => {
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => timeoutController.abort(), 60000); // 60s timeout

      // 合併內部 timeout signal 與外部取消 signal（不使用 AbortSignal.any()，iOS Safari 17.4 才支援）
      let combinedSignal = timeoutController.signal;
      if (externalSignal) {
        if (externalSignal.aborted) {
          clearTimeout(timeoutId);
          const err = new DOMException('Upload cancelled', 'AbortError');
          throw err;
        }
        const outerAbort = () => timeoutController.abort();
        externalSignal.addEventListener('abort', outerAbort, { once: true });
        combinedSignal = timeoutController.signal;
        // clean up listener when timeout fires or fetch completes
        timeoutController.signal.addEventListener('abort', () =>
          externalSignal.removeEventListener('abort', outerAbort), { once: true });
      }

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            // 不設定 Content-Type，讓瀏覽器自動帶 multipart boundary
          },
          body: formData,
          signal: combinedSignal,
        });
        clearTimeout(timeoutId);
        let data;
        try { data = await res.json(); } catch { data = {}; }
        return { httpStatus: res.status, data };
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          // 外部取消（使用者移除 chip）→ 重拋 AbortError 讓呼叫端識別
          if (externalSignal?.aborted) throw error;
          throw new Error('上傳逾時，請稍後再試');
        }
        throw error;
      }
    };

    // HTTP 401 或業務碼 401（與 sendMessage 的判斷條件保持一致）
    const is401 = ({ httpStatus, data }) =>
      httpStatus === 401 || (httpStatus === 200 && data?.code === 401);

    const assertSuccess = ({ httpStatus, data }) => {
      if (data?.code === 200) return;
      const msg = data?.message || `上傳失敗（HTTP ${httpStatus}）`;
      logger.error('聊天', '檔案上傳失敗', { httpStatus, message: msg });
      throw new Error(msg);
    };

    const first = await doFetch(accessToken);

    if (is401(first)) {
      logger.warn('聊天', '檔案上傳遇到 401，進入 refresh token 流程');
      const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

      if (!refreshToken) {
        logger.error('聊天', '上傳回傳 401 且缺少 refresh token');
        this.clearAuthAndRedirectToLogin();
        throw new Error('登入已過期，請重新登入');
      }

      const refreshResult = await this.refreshToken(refreshToken);
      if (!refreshResult.success) {
        logger.error('聊天', '上傳 refresh token 失敗，顯示重新登入提示');
        this.showReauthRequiredDialog(() => this.clearAuthAndRedirectToLogin());
        throw new Error('登入已過期，請重新登入');
      }

      accessToken = refreshResult.token;
      logger.info('聊天', 'refresh token 成功，重新上傳');
      const second = await doFetch(accessToken);
      assertSuccess(second);
      logger.success('聊天', '重試上傳成功', { attachments: second.data?.data?.attachments });
      return second.data.data;
    }

    assertSuccess(first);
    logger.success('聊天', '檔案上傳成功', { attachments: first.data?.data?.attachments });
    return first.data.data;
  }

  /**
   * 刪除尚未綁定至訊息的 session upload asset。
   * 樂觀移除——前端先移除 chip，再呼叫此方法做後端清理。
   * 回傳 { status, data }（HTTP 狀態碼 + 回應 body），**不拋出**，讓呼叫端依狀態碼決定 toast。
   *
   * @param {string} sessionId - 對話 Session ID（chatId）
   * @param {string} uploadId  - attachment URL 的 upload_id query param
   * @param {string|null} [user] - 使用者 ID（可選）
   * @returns {Promise<{status: number, data: object}>}
   */
  async removeUpload(sessionId, uploadId, user = null) {
    logger.info('聊天', '呼叫 removeUpload', { sessionId, uploadId });

    let accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!accessToken) {
      return { status: 401, data: { detail: '尚未登入或 token 遺失' } };
    }

    const body = { session_id: sessionId, upload_id: uploadId };
    if (user) body.user = user;

    const doFetch = async (token) => {
      const res = await fetch(`${this.baseURL}/web/file/uploads/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      let data;
      try { data = await res.json(); } catch { data = {}; }
      return { status: res.status, data };
    };

    const first = await doFetch(accessToken);

    // 401 → token refresh → 重試一次
    if (first.status === 401) {
      logger.warn('聊天', 'removeUpload 遇到 401，進入 refresh token 流程');
      const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      if (refreshToken) {
        const refreshResult = await this.refreshToken(refreshToken);
        if (refreshResult.success) {
          return doFetch(refreshResult.token);
        }
      }
    }

    return first;
  }

  /**
   * 停止指定對話目前執行中的 AI Agent 任務。
   * @param {string} chatId - 對話 ID（即 session_id）
   */
  async stopChat(chatId) {
    logger.info('聊天', '停止 AI Agent 執行', { chatId });
    const accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!accessToken) throw new Error('尚未登入或 token 遺失，請重新登入');
    return this.request(`/web/chat/${chatId}/stop`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      // 後端 /stop 會等到 GenAIus 確認停止才回應（寬限期約 3 秒 + 餘裕），再加上呼叫 GenAIus 本身的時間，
      // 逾時要明顯大於後端等待上限，否則會在後端還在等的時候前端先誤判「停止指令傳送失敗」
      timeout: 20000,
    });
  }

  /**
   * 刪除聊天（軟刪除 ag_tb_conversation.is_deleted，change: remove-legacy-sync）。
   * 僅對已在後端建立過（非 isTemporary）的對話呼叫。
   */
  async deleteChatExternal(chatId) {
    logger.info('聊天', '刪除聊天 API 呼叫', { chatId });
    const accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!accessToken) throw new Error('尚未登入或 token 遺失，請重新登入');
    return this.request(`/web/chat/${chatId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: 10000,
    });
  }

  /**
   * 查詢 AI 回覆暫存（斷線補收，change: ai-response-server-persistence /
   * unify-reply-completion-signal 四態）。
   *
   * 回傳後端四態資料：
   * {@code { status: 'completed'|'processing'|'accepted'|'not_found', assistantMessageId?, replyText?, createdTime? }}
   *
   * <p>業務錯誤（HTTP 200 + ApiResponse.code 非 200，例如 401 token 過期、403 擁有權不符）一律
   * 改為丟出例外，不可回傳給呼叫端。原本直接 return `result.data`，而錯誤形狀的 data 是
   * {@code {_error:true,_message:...}}、沒有 status 欄位，補收輪詢讀到 `status === undefined`
   * 會掉進 not_found 分支，把「查詢失敗」誤判成「後端確定沒收到這則提問」，超過終局時限時直接
   * 顯示「可能未成功送達」——答案其實好端端存在後端（AGUI-1082）。丟例外後由呼叫端的 catch
   * 視為單純的查詢失敗（軟性重試），語意才正確。
   *
   * <p>401 會先嘗試 refresh token 並重試一次（補收是背景輪詢，且觸發點包含 heartbeat，
   * 刻意不比照 /send 在 refresh 失敗時強制跳轉登入頁——背景輪詢不該把使用者踢出畫面，
   * 改為丟例外交給下一個觸發點重試，真正的重新認證交給使用者主動操作的流程處理）。
   *
   * @param {string} chatId 對話 id
   * @param {string} userMessageId 發送時帶入 /send 的提問訊息 id
   * @returns {Promise<object>} 四態資料物件
   * @throws {Error} 查詢失敗或業務碼非 200（帶 businessCode 欄位）
   */
  async getBufferedReply(chatId, userMessageId) {
    logger.info('聊天', '查詢回覆暫存（補收）', { chatId, userMessageId });
    let accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!accessToken) throw new Error('尚未登入或 token 遺失，請重新登入');

    const queryOnce = (token) => this.request(
      `/web/chat/${chatId}/reply?userMessageId=${encodeURIComponent(userMessageId)}`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      }
    );

    let result = await queryOnce(accessToken);

    // token 過期：刷新後重試一次，避免補收查詢在切背景一段時間後因 token 過期而永遠失敗
    if (result?.code === 401) {
      logger.warn('聊天', '補收查詢命中 401，嘗試 refresh token 後重試');
      const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      if (refreshToken) {
        const refreshResponse = await this.request('/web/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        });
        if (refreshResponse?.code === 200 && refreshResponse.data?.token) {
          accessToken = refreshResponse.data.token;
          await appStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
          if (refreshResponse.data.refreshToken) {
            await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshResponse.data.refreshToken);
          }
          logger.info('聊天', 'refresh token 成功，重試補收查詢');
          result = await queryOnce(accessToken);
        } else {
          logger.warn('聊天', '補收查詢的 refresh token 失敗，交由下次觸發點重試');
        }
      }
    }

    if (typeof result?.code === 'number' && result.code !== 200) {
      const err = new Error(result.message || `查詢回覆暫存失敗（code=${result.code}）`);
      err.businessCode = result.code;
      throw err;
    }

    return result?.data ?? result;
  }

  /**
   * 讀取 MCP Apps 互動畫面的 HTML（AGUI-1274，POST /web/chat/{chatId}/mcp-apps/resource）。
   *
   * <p>HTML 不存在對話紀錄裡，每次顯示都即時讀；同一頁面內同一個 `server + uri` 只讀一次
   * （記憶體快取存 Promise，讀取中的併發呼叫共用同一個請求），失敗時移除快取讓「重試」真的重打。
   * 絕不寫入 IndexedDB／localStorage。
   *
   * <p>錯誤：後端以 HTTP 狀態回報（400／403／502…，request() 會丟出帶 http 的例外）；
   * token 過期則比照 getBufferedReply——HTTP 401 或 HTTP 200 + 業務碼 401 時 refresh 後重試一次，
   * refresh 失敗只丟例外（由畫面顯示「重試」），不強制跳轉登入頁。
   *
   * @param {string} chatId 對話 id
   * @param {string} server mcpApp.server（原樣帶回）
   * @param {string} uri mcpApp.resourceUri（ui://...）
   * @returns {Promise<{uri: string, mimeType: string, html: string, meta: object}>}
   */
  getMcpAppResource(chatId, server, uri) {
    const cacheKey = `${server}|${uri}`;
    const cached = this.mcpAppResourceCache.get(cacheKey);
    if (cached) return cached;
    const pending = this.fetchMcpAppResource(chatId, server, uri);
    this.mcpAppResourceCache.set(cacheKey, pending);
    pending.catch(() => {
      // 只移除「自己這一次」的快取，避免誤刪之後重試建立的新請求
      if (this.mcpAppResourceCache.get(cacheKey) === pending) this.mcpAppResourceCache.delete(cacheKey);
    });
    return pending;
  }

  async fetchMcpAppResource(chatId, server, uri) {
    if (!chatId || !server || !uri) throw new Error('互動畫面參數不完整');
    logger.info('聊天', '讀取 MCP App 互動畫面', { chatId, server, uri });
    let accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!accessToken) throw new Error('尚未登入或 token 遺失，請重新登入');

    // 回傳 { result, unauthorized }：把「HTTP 401」與「HTTP 200 + 業務碼 401」統一成同一種判斷
    const queryOnce = async (token) => {
      try {
        const result = await this.request(`/web/chat/${chatId}/mcp-apps/resource`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: JSON.stringify({ server, uri }),
          // 後端讀 relay 的逾時約為連線逾時 + 60 秒，前端略大於它，避免後端還在等時前端先放棄
          timeout: 75000,
        });
        return { result, unauthorized: result?.code === 401 };
      } catch (err) {
        if (err?.http?.status === 401) return { result: err.body, unauthorized: true, error: err };
        throw err;
      }
    };

    let { result, unauthorized, error } = await queryOnce(accessToken);

    if (unauthorized) {
      logger.warn('聊天', '讀取互動畫面命中 401，嘗試 refresh token 後重試');
      const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      const refreshResponse = refreshToken
        ? await this.request('/web/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        })
        : null;
      if (refreshResponse?.code === 200 && refreshResponse.data?.token) {
        accessToken = refreshResponse.data.token;
        await appStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        if (refreshResponse.data.refreshToken) {
          await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshResponse.data.refreshToken);
        }
        ({ result, unauthorized, error } = await queryOnce(accessToken));
      }
      if (unauthorized) {
        if (error) throw error;
        const err = new Error(result?.message || '驗證失敗，無法讀取互動畫面');
        err.businessCode = 401;
        throw err;
      }
    }

    if (typeof result?.code === 'number' && result.code !== 200) {
      const err = new Error(result.message || `讀取互動畫面失敗（code=${result.code}）`);
      err.businessCode = result.code;
      throw err;
    }

    const data = result?.data;
    const mimeType = typeof data?.mimeType === 'string' ? data.mimeType : '';
    const html = typeof data?.html === 'string' ? data.html : '';
    if (mimeType !== MCP_APP_MIME_TYPE) {
      throw new Error(`互動畫面格式不正確（${mimeType || '未提供 mimeType'}）`);
    }
    if (!html) throw new Error('互動畫面沒有內容');
    logger.info('聊天', '讀取 MCP App 互動畫面成功', { uri, htmlLength: html.length });
    return {
      uri: data.uri || uri,
      mimeType,
      html,
      meta: data.meta && typeof data.meta === 'object' ? data.meta : {},
    };
  }

  // 發送訊息到後端 API
  // messageIds（選填）：{ userMessageId, assistantMessageId } 前端預生成的訊息 id，
  // 後端據此啟用回覆暫存（斷線補收）與 idempotency（change: ai-response-server-persistence）
  async sendMessageExternal(chatId, message, attachments = [], signal = null, messageId = null, messageIds = null, sourceChannel = null) {
    logger.info('聊天', '開始發送訊息 API 呼叫', { chatId });

    const canAccessStorage = typeof window !== 'undefined';
    logger.info('聊天', '檢查執行環境是否可存取 storage', { canAccessStorage });
    if (!canAccessStorage) {
      logger.error('聊天', '執行環境無法存取 storage，終止發送');
      throw new Error('瀏覽器環境異常，無法存取憑證');
    }

    logger.info('聊天', '開始讀取 access token / refresh token');
    let accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    let refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    logger.info('聊天', '完成讀取 token', {
      hasAccessToken: Boolean(accessToken),
      hasRefreshToken: Boolean(refreshToken)
    });

    if (!accessToken) {
      logger.error('聊天', '缺少 access token，無法發送訊息');
      throw new Error('尚未登入或 token 遺失，請重新登入');
    }

    const body = {
      message: message,
      ...(messageId ? { messageId } : {}),
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
      // 兩個 id 齊備才帶上（後端也以兩者皆有值才啟用暫存，向後相容）
      ...(messageIds?.userMessageId && messageIds?.assistantMessageId
        ? { userMessageId: messageIds.userMessageId, assistantMessageId: messageIds.assistantMessageId }
        : {}),
      // 推播深連結消費後建立新對話時才會帶（change: notification-deeplink-chat-trigger）
      ...(sourceChannel ? { sourceChannel } : {}),
    };

    const buildSendRequest = (token) => ({
      url: `${this.baseURL}/web/chat/${chatId}/send`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body,
    });

    const sendMessageOnce = async (token) => {
      logger.info('聊天', '準備送出訊息請求', {
        chatId,
        tokenPrefix: token?.slice?.(0, 10)
      });
      const requestResult = await this.request(`/web/chat/${chatId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        includeHttpMeta: true,
        // AI 生成本來就慢：實測寫入平均 115s、最長 160s，與 useChat.js 補收流程的 DEADLINE_MS（300000）對齊。
        // 不可沿用預設的 REQUEST_TIMEOUT_MS（60s），會中斷大多數正常成功的請求。
        timeout: 300000,
        ...(signal ? { signal } : {}),
      });
      logger.info('聊天', '訊息請求已回應', {
        httpStatus: requestResult.http?.status,
        responseCode: requestResult.body?.code
      });

      return {
        response: requestResult.body,
        apiResponse: {
          body: requestResult.body,
          http: requestResult.http,
        },
        requestSnapshot: buildSendRequest(token),
      };
    };

    const buildBusinessError = (response, apiResponse, requestSnapshot) => {
      logger.warn('聊天', '建立業務錯誤物件', {
        status: response?.code,
        message: response?.message
      });
      const err = new Error(response.message || '發送訊息失敗');
      err.requestSnapshot = requestSnapshot;
      err.apiResponse = apiResponse;
      err.businessCode = response.code;
      return err;
    };

    const handleSendSuccess = async (response, requestSnapshot, apiResponse) => {
      logger.success('聊天', '發送訊息成功', {
        chatId,
        messageLength: message.length
      });
      // 若回傳的 data.metadata.tool_list 有值，先存到 pending，由 updateChatMessages 儲存時合併到對應 message
      const innerData = response.data.data;
      const toolList = innerData?.metadata?.tool_list;
      if (Array.isArray(toolList) && toolList.length > 0 && canAccessStorage) {
        logger.info('聊天', '偵測到 tool_list，準備寫入 pending', {
          chatId,
          toolListLength: toolList.length
        });
        try {
          const pendingKey = `pendingToolList_${chatId}`;
          await appStorage.setItem(pendingKey, JSON.stringify({
            message_id: innerData.message_id,
            tool_list: toolList,
            timestamp: Date.now()
          }));
          logger.info('聊天', 'pending tool_list 寫入成功', { pendingKey });
        } catch (e) {
          logger.warn('聊天', '寫入 pending tool_list 失敗', { error: e.message });
        }
      } else {
        logger.info('聊天', '本次回應無需寫入 pending tool_list');
      }
      return {
        data: response.data,
        request: requestSnapshot,
        response: apiResponse,
      };
    };

    let lastRequestSnapshot = buildSendRequest(accessToken);

    try {
      logger.info('聊天', '開始第一次送訊息');
      const firstSend = await sendMessageOnce(accessToken);
      lastRequestSnapshot = firstSend.requestSnapshot;

      if (firstSend.response.code === 200 && firstSend.response.data) {
        logger.info('聊天', '第一次送訊息成功，直接回傳結果');
        return handleSendSuccess(firstSend.response, firstSend.requestSnapshot, firstSend.apiResponse);
      }

      // 先發送訊息，僅在 HTTP 200 且業務碼 401 時才刷新 token 並重送一次
      if (firstSend.apiResponse.http?.status === 200 && firstSend.response.code === 401) {
        logger.warn('聊天', '第一次送訊息命中 401，進入 refresh token 流程');
        if (!refreshToken) {
          logger.error('聊天', '訊息回傳 401 且缺少 refresh token，跳轉到登入頁');
          this.clearAuthAndRedirectToLogin();
          return;
        }

        logger.info('聊天', '開始呼叫 refresh token API');
        const refreshResult = await this.request('/web/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
          includeHttpMeta: true,
        });
        const refreshResponse = refreshResult.body;
        const refreshHttpStatus = refreshResult.http?.status;
        logger.info('聊天', 'refresh token API 已回應', {
          httpStatus: refreshHttpStatus,
          responseCode: refreshResponse?.code
        });

        if (refreshHttpStatus === 200 && refreshResponse.code === 200 && refreshResponse.data?.token) {
          logger.info('聊天', 'refresh token 成功，準備更新本地 token');
          accessToken = refreshResponse.data.token;
          refreshToken = refreshResponse.data.refreshToken || refreshToken;
          await appStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
          await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
          logger.info('聊天', '本地 token 更新完成，準備重送訊息');

          logger.info('聊天', '開始第二次送訊息（重送）');
          const secondSend = await sendMessageOnce(accessToken);
          lastRequestSnapshot = secondSend.requestSnapshot;
          if (secondSend.response.code === 200 && secondSend.response.data) {
            logger.info('聊天', '第二次送訊息成功，回傳結果');
            return handleSendSuccess(secondSend.response, secondSend.requestSnapshot, secondSend.apiResponse);
          }

          logger.error('聊天', '刷新 token 後重送訊息失敗', {
            status: secondSend.response.code,
            message: secondSend.response.message
          });
          throw buildBusinessError(secondSend.response, secondSend.apiResponse, secondSend.requestSnapshot);
        }

        logger.error('聊天', '刷新 token 失敗，跳轉到登入頁', {
          httpStatus: refreshHttpStatus,
          status: refreshResponse.code,
          message: refreshResponse.message
        });
        this.showReauthRequiredDialog(() => this.clearAuthAndRedirectToLogin());
        return;
      }

      logger.warn('聊天', '第一次送訊息失敗且不符合 refresh 條件', {
        httpStatus: firstSend.apiResponse.http?.status,
        status: firstSend.response.code
      });
      logger.error('聊天', '發送訊息失敗', {
        status: firstSend.response.code,
        message: firstSend.response.message
      });
      throw buildBusinessError(firstSend.response, firstSend.apiResponse, firstSend.requestSnapshot);
    } catch (error) {
      logger.warn('聊天', '進入 sendMessageExternal catch 區塊，準備補齊錯誤資訊');
      if (!error.requestSnapshot) {
        error.requestSnapshot = lastRequestSnapshot;
        logger.info('聊天', '補上 requestSnapshot 到錯誤物件');
      }
      if (!error.apiResponse && (error.http || Object.prototype.hasOwnProperty.call(error, 'body') || Object.prototype.hasOwnProperty.call(error, 'rawBody'))) {
        error.apiResponse = {
          body: error.body ?? null,
          http: error.http ?? null,
          ...(error.rawBody !== undefined ? { rawBody: error.rawBody } : {}),
        };
        logger.info('聊天', '補上 apiResponse 到錯誤物件');
      }
      logger.error('聊天', '發送訊息異常', { error: error.message, chatId });
      throw error;
    }
  }
}

// 建立單例實例
const apiService = new ApiService();

// 從 DevTools 呼叫 __clearAuthAndRedirectToLogin() 清除認證並重新載入
if (typeof window !== 'undefined') {
  window.__clearAuthAndRedirectToLogin = () => apiService.clearAuthAndRedirectToLogin();
  window.__showReauthRequiredDialog = () => apiService.showReauthRequiredDialog();
  // 從 DevTools 呼叫 __throwTestError() 拋出測試錯誤（用於驗證 Sentry 上報）
  window.__throwTestError = () => {
    throw new Error('測試錯誤：由 __throwTestError() 觸發');
  };
}

export default apiService;

